import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const EQ_CART_BFCACHE_COOLDOWN_MS = 3000;

const CONVERT_GOALS = {
  drawerOpen: '1004101016',
  checkoutClick: '100157966',
  upsellAdd: '100499464',
  mistiDiffuserAdd: '1004109665',
  freeShippingReached: '100498376',
};

// Live's custom.js fires mistiDiffuserAdd only for this variant: MISTI "Diffuser + 3 Essential Oils".
const MISTI_UPSELL_VARIANT_ID = '55026930024826';

if (!window.__eqCartBfcacheListenerAttached) {
  window.__eqCartBfcacheListenerAttached = true;
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    window.__eqCartUpsellBfcacheCooldownUntil = Date.now() + EQ_CART_BFCACHE_COOLDOWN_MS;
  });
}

/**
 * Preserves the live Nikura Cart Drawer analytics while leaving Horizon's native cart flow untouched.
 *
 * Modes:
 * - drawer: checkout conversion, free-shipping conversion, remove-from-cart, and upsell add tracking.
 * - upsell-list: Leaf view_item_list tracking for the recommendation list rendered by Shopify recommendations.
 */
class EqCartAnalytics extends Component {
  #abortController = null;
  /** @type {{ type: 'button' | 'typed', at: number } | null} How the shopper last changed a cart quantity. */
  #quantitySource = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (this.dataset.mode === 'upsell-list') {
      requestAnimationFrame(() => this.#pushUpsellViewItemList());
      return;
    }

    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    document.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    // cartLinesUpdate always targets cart-items-component, so the input source is recorded first, on window.
    // Typing fires a native change; the plus/minus buttons set the value in code and fire none.
    window.addEventListener('change', this.#recordQuantitySource, { capture: true, signal });
    window.addEventListener('click', this.#recordQuantitySource, { capture: true, signal });

    this.#syncFreeShippingConversion(Number(this.dataset.cartTotalCents || 0));
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  #handleDrawerOpen = (event) => {
    const drawer = event.target instanceof HTMLElement ? event.target : null;
    if (drawer?.id !== 'cart-drawer') return;

    this.#triggerConvert(CONVERT_GOALS.drawerOpen);
  };

  #handleDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('#cart-drawer .theme-drawer__close-button')) {
      this.#pushDataLayer({
        event: 'cart_close', location_id: 'cart',
        cart_item_count: this.#drawerItemCount(),
        cart_value: this.#drawerValue(), device_type: this.#deviceType(),
      });
      return;
    }
    if (!target.closest('#cart-drawer #checkout')) return;

    this.#pushDataLayer({
      event: 'cart_checkout_click', location_id: 'cart',
      currency: this.dataset.currency || 'GBP', value: this.#drawerValue(),
      savings: this.#drawerSavings(), cart_item_count: this.#drawerItemCount(),
      items: Array.from(document.querySelectorAll('#cart-drawer [data-eq-cart-item-sku]'))
        .map((item) => item instanceof HTMLElement ? item.dataset.eqCartItemSku : '')
        .filter(Boolean).join(', '),
    });
    this.#triggerConvert(CONVERT_GOALS.checkoutClick);
  };

  #recordQuantitySource = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('#cart-drawer, [data-eq-cart-analytics-lines]')) return;
    if (event.type === 'change' && target.matches('input[name="updates[]"]')) this.#quantitySource = { type: 'typed', at: performance.now() };
    if (event.type === 'click' && target.closest('button[name="plus"], button[name="minus"]')) this.#quantitySource = { type: 'button', at: performance.now() };
  };

  #handleCartLinesUpdate = (event) => {
    // Horizon debounces quantity input by 300 ms; an older source had no update of its own and must not label this one.
    const recorded = this.#quantitySource;
    const quantitySource = recorded && performance.now() - recorded.at <= 1000 ? recorded.type : null;
    this.#quantitySource = null;
    const source = event.target instanceof Element ? event.target : null;
    // Other cart lists — an offer builder's basket — opt in with `data-eq-cart-analytics-lines` and
    // the same `data-eq-cart-item-*` row attributes the drawer renders.
    const scope = source?.closest('#cart-drawer, [data-eq-cart-analytics-lines]');
    const isDrawerAction = Boolean(scope);
    const upsellItem =
      event.action === 'add' && source
        ? source.closest('[data-eq-cart-upsell-item]')
        : null;

    if (isDrawerAction && event.action === 'remove') {
      const lineId = event.lines?.[0]?.id;
      const cartItem = this.#findCartItem(lineId, scope);
      if (cartItem) this.#pushRemoveFromCart(cartItem);
    }

    const upsellPosition = upsellItem instanceof HTMLElement
      ? this.#upsellVisiblePosition(upsellItem)
      : null;

    if (upsellItem instanceof HTMLElement) {
      this.#trackUpsellAdd(upsellItem);
    }

    // Capture the original row before Horizon morphs the cart. The native
    // success promise—not a guessed timeout—confirms changes actually landed.
    const changedRow = isDrawerAction && (event.action === 'update' || event.action === 'remove')
      ? this.#findCartItem(event.lines?.[0]?.id, scope)
      : null;
    const before = changedRow ? {
      item_id: changedRow.dataset.eqCartItemSku || '',
      item_name: changedRow.dataset.eqCartItemTitle || '',
      item_variant: changedRow.dataset.eqCartItemVariant || '',
      quantity: Number(changedRow.dataset.eqCartItemQuantity || 0),
      price: this.#centsToAmount(changedRow.dataset.eqCartItemPriceCents),
      value: this.#centsToAmount(changedRow.dataset.eqCartItemFinalLineCents),
    } : null;

    event.promise
      ?.then(({ cart }) => {
        if (before) {
          const qtyAfter = Number(event.lines?.[0]?.quantity || 0);
          const cartTotal = Number(cart?.cost?.totalAmount?.amount);
          // Live's new custom cart_remove_item uses unit price times quantity.
          const removedValue = Math.round(before.price * before.quantity * 100) / 100;
          if (event.action === 'remove' || qtyAfter === 0) {
            this.#pushDataLayer({ ecommerce: null });
            this.#pushDataLayer({
              event: 'cart_remove_item', location_id: 'cart',
              currency: this.dataset.currency || 'GBP', value: removedValue,
              item_id: before.item_id, item_name: before.item_name,
              item_variant: before.item_variant, quantity: before.quantity,
              price: before.price, cart_value_after: Number.isFinite(cartTotal) ? cartTotal : 0,
              // Live nk_cart_events.js includes GA4 ecommerce on this custom event.
              ecommerce: {
                currency: this.dataset.currency || 'GBP',
                value: removedValue,
                items: [{
                  item_id: before.item_id, item_name: before.item_name,
                  item_variant: before.item_variant, price: before.price,
                  quantity: before.quantity,
                }],
              },
            });
          } else if (qtyAfter !== before.quantity) {
            this.#pushDataLayer({
              event: 'cart_quantity_change', location_id: 'cart',
              button: quantitySource === 'typed' ? 'typed' : qtyAfter > before.quantity ? 'plus' : 'minus',
              item_id: before.item_id, item_name: before.item_name,
              quantity_before: before.quantity, quantity_after: qtyAfter,
              cart_value_after: Number.isFinite(cartTotal) ? cartTotal : 0,
            });
          }
        }
        if (upsellItem instanceof HTMLElement) {
          const item = this.#getUpsellItemData(upsellItem, 0);
          const position = upsellPosition;
          this.#pushDataLayer({
            event: 'cart_upsell_add', location_id: 'cart_upsell',
            item_list_id: 'customers_also_bought', item_list_name: 'Customers Also Bought',
            item_id: upsellItem.dataset.eqCartUpsellSku || item.item_id,
            item_name: item.item_name, price: item.price, index: position,
          });
        }
        const amount = Number(cart?.cost?.totalAmount?.amount);
        if (Number.isFinite(amount)) {
          this.#syncFreeShippingConversion(Math.round(amount * 100));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (upsellItem instanceof HTMLElement) this.#setLeafUpsellFlag(false);
      });
  };

  /**
   * @param {string | number | undefined} lineId
   * @param {Element | null | undefined} scope
   * @returns {HTMLElement | null}
   */
  #findCartItem(lineId, scope) {
    if (!lineId || !scope) return null;

    const rows = scope.querySelectorAll('[data-eq-cart-item-title][data-key]');
    return (
      Array.from(rows).find((row) => row instanceof HTMLElement && row.dataset.key === String(lineId)) || null
    );
  }

  /** @param {HTMLElement} item */
  #pushRemoveFromCart(item) {
    const price = this.#centsToAmount(item.dataset.eqCartItemPriceCents);
    const value = this.#centsToAmount(item.dataset.eqCartItemFinalLineCents);
    const quantity = Number(item.dataset.eqCartItemQuantity || 1);
    // The drawer reports the SKU as the item id. A row that names its own id (the variant, as the
    // old offer builders did) keeps the SKU alongside it instead.
    const itemId = item.dataset.eqCartItemId;
    const ids = itemId
      ? { item_id: itemId, item_sku: item.dataset.eqCartItemSku || '' }
      : { item_id: item.dataset.eqCartItemSku || '' };

    this.#pushDataLayer({ ecommerce: null });
    this.#pushDataLayer({
      event: 'remove_from_cart',
      ecommerce: {
        currency: this.dataset.currency || 'GBP',
        value,
        items: [
          {
            item_name: item.dataset.eqCartItemTitle || '',
            ...ids,
            price,
            quantity: Number.isFinite(quantity) ? quantity : 1,
            item_brand: item.dataset.eqCartItemVendor || '',
            item_category: item.dataset.eqCartItemCategory || '',
            item_variant: item.dataset.eqCartItemVariant || '',
          },
        ],
      },
    });
  }

  /** @param {HTMLElement} item */
  #trackUpsellAdd(item) {
    this.#setLeafUpsellFlag(true);
    this.#triggerConvert(CONVERT_GOALS.upsellAdd);

    if (item.dataset.variantId === MISTI_UPSELL_VARIANT_ID) {
      this.#triggerConvert(CONVERT_GOALS.mistiDiffuserAdd);
    }

    if (item.dataset.eqLeafListItem !== 'true') return;

    const data = this.#getUpsellItemData(item, 0);
    if (!data.item_name) return;

    this.#pushDataLayer({ ecommerce: null });
    this.#pushDataLayer({
      event: 'add_to_cart_upsell',
      ecommerce: {
        currency: this.dataset.currency || 'GBP',
        value: data.price,
        items: [
          {
            ...data,
            item_list_name: 'Cart Upsell Recommendations',
            item_list_id: 'cart_upsell',
          },
        ],
      },
    });
  }

  #pushUpsellViewItemList() {
    const cooldownUntil = Number(window.__eqCartUpsellBfcacheCooldownUntil || 0);
    if (Date.now() < cooldownUntil) return;

    const drawerDialog = document.querySelector('#cart-drawer dialog');
    if (drawerDialog instanceof HTMLDialogElement && !drawerDialog.open) return;

    const items = Array.from(this.querySelectorAll('[data-eq-cart-upsell-item][data-eq-leaf-list-item="true"]'))
      .filter((item) => item instanceof HTMLElement && item.getClientRects().length > 0)
      .map((item, index) => this.#getUpsellItemData(/** @type {HTMLElement} */ (item), index))
      .filter((item) => item.item_name);

    if (items.length === 0) return;

    const signature = items.map((item) => item.item_id).join('|');
    const now = Date.now();
    const lastSignature = window.__eqCartUpsellViewSignature || '';
    const lastTime = Number(window.__eqCartUpsellViewTime || 0);

    if (signature === lastSignature && now - lastTime < 2000) return;

    window.__eqCartUpsellViewSignature = signature;
    window.__eqCartUpsellViewTime = now;

    this.#pushDataLayer({ ecommerce: null });
    this.#pushDataLayer({
      event: 'view_item_list_upsell',
      ecommerce: {
        item_list_id: 'cart_upsell',
        item_list_name: 'Cart Upsell Recommendations',
        items,
      },
    });
  }

  /**
   * @param {HTMLElement} item
   * @param {number} index
   */
  #getUpsellItemData(item, index) {
    return {
      item_name: item.dataset.productTitle || '',
      item_id: item.dataset.variantId || '',
      price: this.#centsToAmount(item.dataset.price),
      index,
      quantity: 1,
    };
  }

  /** @param {number} cartTotalCents */
  #syncFreeShippingConversion(cartTotalCents) {
    const threshold = Number(this.dataset.freeShippingThresholdCents || 0);
    if (!threshold) return;

    const reached = cartTotalCents >= threshold;

    if (reached && !window.__eqCartFreeShippingGoalReached) {
      window.__eqCartFreeShippingGoalReached = true;
      this.#triggerConvert(CONVERT_GOALS.freeShippingReached);
    } else if (!reached) {
      window.__eqCartFreeShippingGoalReached = false;
    }
  }

  /** @param {boolean} active */
  #setLeafUpsellFlag(active) {
    window._leafCartUpsellATC = active;
    this.#pushDataLayer({ cart_upsell_atc: active });

    if (active && window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('leaf_cart_upsell_atc', {});
    }
  }

  /** @param {string} goalId */
  #triggerConvert(goalId) {
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['triggerConversion', goalId]);
  }

  /** @param {Record<string, any>} payload */
  #pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /** Live analytics uses a 1-based position among visible recommendation cards. */
  #upsellVisiblePosition(selected) {
    const cards = Array.from(document.querySelectorAll('#cart-drawer [data-eq-cart-upsell-item]'))
      .filter((card) => card instanceof HTMLElement && card.getClientRects().length > 0);
    const position = cards.indexOf(selected);
    if (position >= 0) return position + 1;

    // A re-render may detach the clicked card before the cart update completes.
    // If so, use the original list's DOM position instead of hardcoding zero.
    const siblingCards = Array.from(selected.parentElement?.querySelectorAll('[data-eq-cart-upsell-item]') || []);
    const siblingPosition = siblingCards.indexOf(selected);
    return siblingPosition >= 0 ? siblingPosition + 1 : null;
  }

  #drawerRows() {
    return Array.from(document.querySelectorAll('#cart-drawer [data-eq-cart-item-quantity]'))
      .filter((item) => item instanceof HTMLElement);
  }

  #drawerItemCount() {
    const declared = document.querySelector('#cart-drawer [data-eq-cart-item-count]');
    if (declared instanceof HTMLElement) return Number(declared.dataset.eqCartItemCount || 0);
    return this.#drawerRows().reduce((sum, item) => sum + Number(item.dataset.eqCartItemQuantity || 0), 0);
  }

  #drawerValue() {
    const declared = document.querySelector('#cart-drawer [data-eq-cart-total-cents]');
    if (declared instanceof HTMLElement) return this.#centsToAmount(declared.dataset.eqCartTotalCents);
    return this.#drawerRows().reduce((sum, item) => sum + this.#centsToAmount(item.dataset.eqCartItemFinalLineCents), 0);
  }

  #drawerSavings() {
    const declared = document.querySelector('#cart-drawer [data-eq-cart-savings-cents]');
    return declared instanceof HTMLElement ? this.#centsToAmount(declared.dataset.eqCartSavingsCents) : 0;
  }

  #deviceType() {
    return window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
  }

  /** @param {string | undefined} cents */
  #centsToAmount(cents) {
    const value = Number(cents || 0);
    return Number.isFinite(value) ? value / 100 : 0;
  }
}

if (!customElements.get('eq-cart-analytics')) {
  customElements.define('eq-cart-analytics', EqCartAnalytics);
}
